import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ItemsBatchAddSchema,
  ItemsBatchEditSchema,
  type ItemsBatchAdd,
  type ItemsBatchEdit,
  type TimelineItemWithCommentCount,
} from "@travel/shared";
import { api, ApiError } from "../../api/client";
import { BottomSheet } from "../common/BottomSheet";
import { Button } from "../common/Button";

type Mode = "add" | "edit";

interface Props {
  open: boolean;
  planId: string;
  items: TimelineItemWithCommentCount[];
  onClose: () => void;
}

const ADD_SAMPLE = `{
  "items": [
    {
      "title": "스타페리 탑승",
      "description": "센트럴 → 침사추이",
      "startDate": "2026-05-02T10:00:00.000Z",
      "endDate": "2026-05-02T10:30:00.000Z",
      "googleMapLink": "https://maps.google.com/?q=Star+Ferry"
    }
  ]
}`;

export function JsonItemsDialog({ open, planId, items, onClose }: Props) {
  const [mode, setMode] = useState<Mode>("add");
  const [json, setJson] = useState(ADD_SAMPLE);
  const [error, setError] = useState<string | null>(null);

  const editTemplate = useMemo(
    () =>
      JSON.stringify(
        {
          items: items.map((it) => ({
            id: it.id,
            title: it.title,
            description: it.description,
            startDate: it.startUtc,
            endDate: it.endUtc,
            googleMapLink: it.googleMapLink ?? "",
          })),
        },
        null,
        2,
      ),
    [items],
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    setJson(mode === "add" ? ADD_SAMPLE : editTemplate);
  }, [open, mode, editTemplate]);

  const qc = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (body: ItemsBatchAdd) => api.batchAddItems(planId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan", planId] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    },
    onError: handleApiError,
  });

  const editMutation = useMutation({
    mutationFn: (body: ItemsBatchEdit) => api.batchEditItems(planId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan", planId] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    },
    onError: handleApiError,
  });

  function handleApiError(e: unknown) {
    if (e instanceof ApiError && e.issues) {
      setError(e.issues.map((i) => `${i.path}: ${i.message}`).join("\n"));
    } else if (e instanceof Error) {
      setError(e.message);
    } else {
      setError("저장에 실패했습니다.");
    }
  }

  function onSave() {
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      setError(`JSON 형식 오류: ${(e as Error).message}`);
      return;
    }
    if (mode === "add") {
      const result = ItemsBatchAddSchema.safeParse(parsed);
      if (!result.success) {
        setError(
          result.error.issues
            .map((i) => `${i.path.join(".")}: ${i.message}`)
            .join("\n"),
        );
        return;
      }
      addMutation.mutate(result.data);
    } else {
      const result = ItemsBatchEditSchema.safeParse(parsed);
      if (!result.success) {
        setError(
          result.error.issues
            .map((i) => `${i.path.join(".")}: ${i.message}`)
            .join("\n"),
        );
        return;
      }
      editMutation.mutate(result.data);
    }
  }

  const pending = addMutation.isPending || editMutation.isPending;

  return (
    <BottomSheet open={open} onClose={onClose} title="JSON으로 일정 추가/수정">
      <div className="px-4 space-y-3 pb-4">
        <div role="tablist" className="flex gap-1 p-1 bg-slate-100 rounded-xl">
          <TabButton active={mode === "add"} onClick={() => setMode("add")}>
            추가
          </TabButton>
          <TabButton active={mode === "edit"} onClick={() => setMode("edit")}>
            수정
          </TabButton>
        </div>
        <p className="text-sm text-slate-500">
          {mode === "add"
            ? "items 배열에 새 일정을 추가하세요. 시간은 ISO-8601 UTC(끝의 Z)로 입력합니다."
            : "수정할 항목의 id와 바꿀 필드만 남기거나 그대로 편집해 저장하세요. 누락된 id는 변경되지 않습니다."}
        </p>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          className="w-full h-72 p-3 border border-slate-300 rounded-xl text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono"
          spellCheck={false}
        />
        {mode === "edit" && items.length === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-3">
            수정할 일정이 없습니다. 먼저 일정을 추가하세요.
          </p>
        )}
        {error && (
          <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
            {error}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            취소
          </Button>
          <Button
            className="flex-1"
            onClick={onSave}
            disabled={pending || (mode === "edit" && items.length === 0)}
          >
            {pending ? "저장 중…" : "저장"}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
        active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}
